import { TPaymentAccordionInstance } from 'widgets/checkout/PaymentAccordion';
import { IPaymentOptionAccordionItem } from 'widgets/checkout/PaymentAccordionItem';
import { TPaymentAccordionItemInstance } from 'widgets/checkout/PaymentAccordionItem';
import { TWidget } from 'widgets/Widget';
import { submitFormJson } from 'widgets/toolbox/ajax';

/**
 * @param AddressCheckoutForm Base widget for extending
 * @returns AddressCheckoutForm Form
 */
function PaymentBillingFormClassCreator(AddressCheckoutForm: import('widgets/checkout/AddressCheckoutForm').TAddressCheckoutForm) {
    /**
     * @category widgets
     * @subcategory checkout
     * @class PaymentBillingForm
     * @augments AddressCheckoutForm
     * @classdesc Represents checkout billing form with next features:
     * 1. Handles form submission
     * 2. Handles form submission server response
     * 3. Clears form sensitive data
     * 4. Toggles use shipping address functionality
     * 5. Prepares address options for an address list
     *
     * Widget has next relationship:
     * * {@link PaymentAccordion#validate} is used for payment validation before form submission
     * * {@link PaymentAccordion#process} is used before form submission to get payment method
     * * {@link PaymentAccordion#cleanupSensitiveData} is used to clear sensitive payment data
     * * {@link PaymentAccordion#handleError} is used to show payment method errors
     * * {@link Label#toggle} is used to show/hide use shipping block summary
     * * {@link InputCheckbox#getValue} is used to get state of `use shipping address` checkbox
     * @example <caption>Checkout billing form</caption>
     * <form
     *     class="b-form"
     *     autocomplete="on"
     *     novalidate
     *     method="POST"
     *     action="${URLUtils.url('CheckoutServices-SubmitPayment')}"
     *     data-widget="billingForm"
     *     data-use-address-type="${!empty(pdict.customer.addresses) ? 'useBilling' : 'useShipping'}"
     *     data-address-selector="billingAddressSelector"
     *     data-widget-event-submit="billingSubmitted"
     *     data-forward-to-parent="submitCurrentStep:toggleNextPaymentButton"
     *     data-address-fields="${pdict.billingAddressFields.join(',')}"
     *     data-form-definition-url="${URLUtils.url('Forms-FormDefinition', 'form', 'billing')}"
     *     data-shipping-skipped="${Boolean(pdict.skipShippingStep)}"
     *     data-tau="checkout_billing"
     *     <isprint value=${pdict.forms.billingForm.attributes} encoding="off" />
     *     data-address-autocomplete-enabled="${!!require('util/sitepref').getValue('addressSuggestionProvider').value}"
     *>
     *    <fieldset class="b-form_set">
     *      <legend
     *          class="b-form_set-label" ${pdict.order.totals.isZeroTotal || pdict.hidePaymentOptions ? 'hidden' : ''}
     *          data-ref="paymentOptionsLegend"
     *      >
     *          ${Resource.msg('billing.fieldset.paymentchoose', 'checkout', null)}
     *      </legend>
     *      <div
     *          role="alert"
     *          class="b-form-message b-message m-error"
     *          hidden="hidden"
     *          data-ref="errorMessageLabel"
     *      ></div>
     *      <div
     *          class="b-form-message b-message m-error"
     *          hidden="hidden"
     *          data-ref="paymentErrorMessage"
     *          role="alert"
     *      ></div>
     *
     *      <isinclude template="checkout/billing/paymentOptions" />
     *    </fieldset>
     *
     *    <div
     *        class="b-billing"
     *        data-ref="billingFormWrapper"
     *        hidden="hidden"
     *    >
     *      ... billing form content
     *    </div>
     * </form>
     */
    class PaymentBillingForm extends AddressCheckoutForm {
        prefs() {
            return {
                errorMessageLabelRef: 'paymentErrorMessage',
                shippingSkipped: false,
                skipSummaryStep: false,
                useAddressType: 'useShipping',
                saveAddressCheckbox: 'dwfrm_billing_addressFields_saveAddress',
                ...super.prefs()
            };
        }

        /**
         * @description Widget initialization
         */
        init(): void {
            super.init();

            this.eventBus().on('order.updated', 'onOrderUpdated');
        }

        /**
         * @description Submit form handler
         */
        submit(): void {
            if (this.isValid()) {
                this.handleSubmit();
            } else {
                this.validate();
            }
        }

        /**
         * @description Update blocks visibility based on flag
         * @param order - Order model
         */
        onOrderUpdated(order) {
            this.ref('paymentOptionsLegend').toggle(order.hidePaymentOptions !== true);
        }

        /**
         * @description Validate form and process submission
         * @returns Promise object represents server response for billing form submission
         */
        handleSubmit(): Promise<void | Record<string, unknown>> | Promise<null> {
            if (!this.isChildrenValid() || this.submitting) {
                return Promise.resolve(null);
            }

            this.submitting = true;
            this.busy();
            this.ref(this.prefs().submitButton).disable();
            this.ref(this.prefs().errorMessageLabelRef).hide();
            const submissionResult = this.getById('payment', (payment: TPaymentAccordionInstance) => {
                return payment
                    .process({ billingAddressFields: this.getFormFieldsShort() })
                    .then(data => {
                        return submitFormJson(
                            this.getFormUrl(),
                            data as Record<string, string | number | boolean | undefined>,
                            this.ref('self').attr('method') === 'GET' ? 'GET' : 'POST'
                        );
                    })
                    .then((response: TServerResponse) => {
                        this.onSubmitted(response);

                        return response;
                    })
                    .catch((err?: TResponseError | Error) => {
                        if (err instanceof Error) {
                            if (err.message) {
                                this.setError(err.message);
                            }
                        } else if (err?.errorMessage) {
                            this.setError(err.errorMessage);
                        }
                    })
                    .finally(this.afterSubmission.bind(this));
            });

            return submissionResult || Promise.resolve(null);
        }

        /**
         * @description Clear payment sensitive data
         * @param stage Stage on which data is going to be cleared
         */
        cleanupSensitiveData(stage: string) {
            this.getById('payment', (payment: TPaymentAccordionInstance) => {
                payment.data('cleanupSensitiveData', stage);
            });
        }

        /**
         * @description Process form submission response
         * @param data - Server response object
         * @param data.success - is it success
         * @param data.serverErrors - server errors
         * @param data.fieldErrors - field errors
         * @param data.cartError - is it cart error
         * @param data.redirectUrl - redirect url
         */
        onSubmitted(data: TServerResponse) {
            if (!data.success) {
                if (data?.serverErrors?.length) {
                    this.setError(data.serverErrors.join(''));
                } else if (data?.error && data?.message) {
                    this.setError(String(data.message));
                }

                if (data.fieldErrors) {
                    this.handleFieldError(data.fieldErrors);
                }

                if (data.cartError && data.redirectUrl) {
                    window.location.assign(data.redirectUrl);
                }

                if (data.disablePaymentMethod) {
                    this.getById('payment', (paymentAccordion: TPaymentAccordionInstance) => {
                        const currentPayment = paymentAccordion.currentItem as TPaymentAccordionItemInstance;

                        currentPayment?.ref('self').get()?.classList.add('m-disabled');
                    });
                }
            } else {
                super.onSubmitted(data);

                if (!this.prefs().skipSummaryStep) {
                    this.cleanupSensitiveData('submission');
                }

                this.hidePaymentError();
            }

            this.emitBillingStepSubmitted(data);
        }

        /**
         * @description Emit billing submitted analytics event with checkoutMgr checkoutType when present
         * @param data - Server response object
         */
        emitBillingStepSubmitted(data: TServerResponse) {
            // PaymentBillingForm emits the AJAX response (not checkoutMgr). Copy checkoutType
            // from checkoutMgr so GTM add_payment_info keeps UCP/express attribution.
            const checkoutMgrNode = this.ref('self').get()?.closest('[data-widget="checkoutMgr"]');
            const checkoutType = checkoutMgrNode?.getAttribute('data-checkout-type');

            if (checkoutType) {
                data.config = Object.assign({}, data.config, { checkoutType: checkoutType });
            }

            this.eventBus().emit('checkout.billing.step.submitted', data);
        }

        /**
         * @description Handles form field error
         * @param entries form entries
         */
        handleFieldError(entries: TFieldError | Array<TFieldError>) {
            Object.entries(entries).forEach(([name, errorMsg]) => {
                this.getById('payment', (payment: TPaymentAccordionInstance) => {
                    payment.handleError(name, errorMsg);
                });
            });
        }

        /**
         * @description Activate billing step
         * @param param - Activation params object
         * @param param.order - Order object
         * @param param.customer - Customer object
         */
        activate({ order, customer }) {
            super.activate({ order, customer });
            this.cleanupSensitiveData('activation');
            this.getById('payment', (paymentAccordion: TPaymentAccordionInstance) => {
                paymentAccordion.activate();
            });
        }

        /**
         * @description Clear payment error
         */
        hidePaymentError() {
            this.clearError(this.prefs().errorMessageLabelRef);
        }

        /**
         * @description Show payment error
         * @param widget - Widget emitted event
         * @param err - Error msg
         */
        showPaymentError(widget: TWidget, err: string) {
            if (!err) {
                return;
            }

            this.setError(err);
        }

        /**
         * @description Returns payment option accordion items
         */
        getPaymentOptionItems() {
            let items: Array<IPaymentOptionAccordionItem> = [];

            this.getById('payment', (payment: TPaymentAccordionInstance) => {
                items = <Array<IPaymentOptionAccordionItem>>payment.items;
            });

            return items;
        }

        /**
         * @description Returns current open payment option accordion item
         */
        getCurrentPaymentOptionItem() {
            return this.getPaymentOptionItems().find(item => item.isPanelOpen);
        }

        /**
         * @description Toggle Billing Form state based on passed state
         * @listens togglebillingform
         */
        toggleBillingForm() {
            // Override to prevent the call since there is no billing form here anymore.
        }

        /**
         * @description Set Form or Form fields errors
         * @param msg - Generic error message, if no custom message - show generic one
         * @param scrollToError - defines using of scroll to error
         * @param refElem - RefElement id
         */
        setError(msg: string, scrollToError = true, refElem: string | null = null) {
            refElem = refElem ?? this.prefs().errorMessageLabelRef;

            return super.setError(msg, scrollToError, refElem);
        }
    }

    return PaymentBillingForm;
}

export type TPaymentBillingForm = ReturnType<typeof PaymentBillingFormClassCreator>;
export type TPaymentBillingFormInstance = InstanceType<TPaymentBillingForm>;

export default PaymentBillingFormClassCreator;
